package com.dotdrives.web.pages.donations

import com.dotdrives.controller.DonationDetailController
import com.dotdrives.controller.DonationDetailUiState
import com.dotdrives.controller.DonationsLedgerController
import com.dotdrives.controller.DonationsLedgerUiState
import com.dotdrives.controller.NeedsAttentionController
import com.dotdrives.controller.NeedsAttentionUiState
import com.dotdrives.controller.QuickRecordController
import com.dotdrives.controller.QuickRecordUiState
import com.dotdrives.controller.YearEndReceiptsController
import com.dotdrives.controller.YearEndReceiptsUiState
import com.dotdrives.model.manualGiftFields
import com.dotdrives.design.DotButtonVariant
import com.dotdrives.model.Donation
import com.dotdrives.web.shell.NavItem
import com.dotdrives.web.ui.DotButton
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.b
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.select
import react.dom.html.ReactHTML.span
import react.useEffect
import react.useState
import web.cssom.ClassName
import web.html.ButtonType
import web.html.InputType
import web.html.button as buttonType
import web.html.text
import kotlin.js.Date

external interface DonationsPageProps : Props {
    var giftController: com.dotdrives.controller.WorkflowController
    var giftState: com.dotdrives.controller.WorkflowState
    var orgId: String
    var userId: String
    var currentUserRole: String
    var ledgerController: DonationsLedgerController
    var ledgerState: DonationsLedgerUiState
    var attentionController: NeedsAttentionController
    var attentionState: NeedsAttentionUiState
    var detailController: DonationDetailController
    var detailState: DonationDetailUiState
    var quickRecordController: QuickRecordController
    var quickRecordState: QuickRecordUiState
    var yearEndController: YearEndReceiptsController
    var yearEndState: YearEndReceiptsUiState
    var canWrite: Boolean
    var onNavigate: (NavItem) -> Unit
}

val DonationsPage = FC<DonationsPageProps> { props ->
    val ledger = props.ledgerState
    val (view, setView) = useState("gifts")
    val (selectedDonationId, setSelectedDonationId) = useState<String?>(null)
    val (attentionOpen, setAttentionOpen) = useState(false)
    val (quickRecordOpen, setQuickRecordOpen) = useState(false)
    val (yearEndOpen, setYearEndOpen) = useState(false)

    useEffect(props.ledgerController) {
        props.ledgerController.load()
    }
    useEffect(props.attentionController) {
        props.attentionController.load()
    }

    useEffect(props.giftState.result) {
        if (props.giftState.result?.get("donation_id") != null) {
            setQuickRecordOpen(false)
            props.ledgerController.load()
            props.attentionController.load()
        }
    }

    fun openDonation(donation: Donation) {
        setSelectedDonationId(donation.id)
        props.detailController.open(donation.id)
    }

    DonationsStyles()
    com.dotdrives.web.ui.WorkflowFeedback { controller = props.giftController; state = props.giftState }

    div {
        className = ClassName("donations-page")

        div {
            className = ClassName("donations-header")
            div {
                className = ClassName("donations-header-row")
                div { className = ClassName("donations-title"); +"Donations" }
                div {
                    className = ClassName("donations-topbar-actions")
                    DotButton {
                        text = "⇩ Export CSV"
                        variant = DotButtonVariant.Secondary
                        className = "donations-topbar-button"
                        onClick = { exportDonationsCsv(ledger.donations) }
                        enabled = view == "gifts" && ledger.donations.isNotEmpty()
                    }
                    button {
                        type = ButtonType.buttonType
                        className = ClassName(
                            "donations-attention-button" + if (ledger.syncErrorCount > 0) " donations-attention-button-active" else "",
                        )
                        onClick = { setAttentionOpen(true) }
                        +"Needs attention"
                        if (props.attentionState.items.isNotEmpty()) {
                            span {
                                className = ClassName("donations-attention-badge")
                                +props.attentionState.items.size.toString()
                            }
                        }
                    }
                    if (props.canWrite) {
                        DotButton {
                            text = "🧾 Year-end receipts"
                            variant = DotButtonVariant.Secondary
                            className = "donations-topbar-button"
                            onClick = {
                                props.yearEndController.load(currentYear())
                                setYearEndOpen(true)
                            }
                        }
                    }
                    if (props.canWrite) {
                        DotButton {
                            text = "+ Record gift"
                            className = "donations-topbar-button"
                            onClick = {
                                props.quickRecordController.start()
                                setQuickRecordOpen(true)
                            }
                        }
                    }
                }
            }

            div {
                className = ClassName("donations-view-tabs")
                listOf("gifts" to "Gifts", "recurring" to "Recurring schedules").forEach { (value, label) ->
                    button {
                        type = ButtonType.buttonType
                        className = ClassName("donations-view-tab" + if (view == value) " donations-view-tab-active" else "")
                        ariaPressed = (view == value).unsafeCast<react.dom.aria.AriaPressed>()
                        onClick = { setView(value) }
                        +label
                    }
                }
            }
            if (view == "gifts") div {
                className = ClassName("donations-search-row")
                input {
                    className = ClassName("donations-search")
                    type = InputType.text
                    value = ledger.query
                    placeholder = "⌕ Search donations…"
                    ariaLabel = "Search donations"
                    onChange = { props.ledgerController.search(it.currentTarget.value) }
                }
                select {
                    className = ClassName("donations-filter-select")
                    ariaLabel = "Target"
                    value = ledger.targetFilter ?: ""
                    onChange = { props.ledgerController.setTargetFilter(it.currentTarget.value.takeIf(String::isNotBlank)) }
                    option { value = ""; +"All targets" }
                    ledger.targets.forEach { target ->
                        option { key = target.id.unsafeCast<Key>(); value = target.id; +target.name }
                    }
                }
                select {
                    className = ClassName("donations-filter-select")
                    ariaLabel = "Source"
                    value = ledger.sourceFilter ?: ""
                    onChange = { props.ledgerController.setSourceFilter(it.currentTarget.value.takeIf(String::isNotBlank)) }
                    option { value = ""; +"All sources" }
                    listOf("manual", "form", "subscription", "everyware").forEach { source ->
                        option { key = source.unsafeCast<Key>(); value = source; +sourceLabel(source) }
                    }
                }
                div {
                    className = ClassName("donations-month-group")
                    button {
                        type = ButtonType.buttonType
                        className = ClassName("donations-month-nav")
                        ariaLabel = "Previous month"
                        onClick = { props.ledgerController.goToPreviousMonth() }
                        +"←"
                    }
                    span { className = ClassName("donations-month-label"); +monthAbbrev(ledger.monthStart) }
                    button {
                        type = ButtonType.buttonType
                        className = ClassName("donations-month-nav")
                        ariaLabel = "Next month"
                        onClick = { props.ledgerController.goToNextMonth() }
                        +"→"
                    }
                }
                div {
                    className = ClassName("donations-month-total")
                    +"${monthAbbrev(ledger.monthStart)} TOTAL "
                    if (props.canWrite) {
                        b { +formatMoneyFull(ledger.netTotal) }
                        +" · ${ledger.giftCount} gift${if (ledger.giftCount == 1) "" else "s"}"
                    } else {
                        b { +"—" }
                    }
                }
            }
        }

        if (view == "recurring") RecurringSchedulesPanel {
            userId = props.userId
            orgId = props.orgId
            giverId = null
        }
        if (view == "gifts") DonationLedgerList {
            loading = ledger.loading
            donations = ledger.donations
            hasActiveFilters = ledger.hasActiveFilters
            error = ledger.error
            canWrite = props.canWrite
            onRetry = props.ledgerController::retry
            onOpen = ::openDonation
            onRecordGift = {
                props.quickRecordController.start()
                setQuickRecordOpen(true)
            }
            onClearFilters = {
                props.ledgerController.search("")
                props.ledgerController.setTargetFilter(null)
                props.ledgerController.setSourceFilter(null)
            }
            onCreateGivingForm = {
                props.onNavigate(NavItem.Forms)
            }
        }
    }

    if (selectedDonationId != null) {
        DonationDetailPanel {
            state = props.detailState
            controller = props.detailController
            canWrite = props.canWrite
            onClose = { setSelectedDonationId(null) }
        }
    }

    if (attentionOpen) {
        NeedsAttentionPanel {
            state = props.attentionState
            controller = props.attentionController
            canWrite = props.canWrite
            onClose = { setAttentionOpen(false) }
            onOpenDonation = { donation ->
                setAttentionOpen(false)
                openDonation(donation)
            }
            onOpenDrives = {
                setAttentionOpen(false)
                props.onNavigate(NavItem.Drives)
            }
        }
    }

    if (quickRecordOpen) {
        QuickRecordDialog {
            controller = props.quickRecordController
            state = props.quickRecordState
            targets = ledger.targets
            externalError = props.giftState.error
            onRetryWorkflow = if (props.giftState.pending != null) props.giftController::retry else null
            submitting = props.giftState.busy || props.giftState.pending != null
            onSubmit = {
                props.quickRecordController.validatedDonation()?.let {
                    props.giftController.perform("workflows", "manual.gift", it.manualGiftFields())
                }
            }
            onCancel = { setQuickRecordOpen(false) }
            onSaved = {
                setQuickRecordOpen(false)
                props.ledgerController.load()
                props.attentionController.load()
            }
        }
    }

    if (yearEndOpen) {
        YearEndReceiptsDialog {
            orgId = props.orgId; userId = props.userId; currentUserRole = props.currentUserRole
            controller = props.yearEndController
            state = props.yearEndState
            onClose = { setYearEndOpen(false) }
        }
    }
}

private fun currentYear(): Int = Date().getFullYear()

private fun exportDonationsCsv(donations: List<Donation>) {
    val header = "Giver,Target,Source,Date,Amount,Payment Type,QuickBooks,Memo"
    val rows = donations.joinToString("\r\n") { donation ->
        listOf(
            donation.giverDisplayName,
            donation.target.orEmpty(),
            sourceLabel(donation.source),
            dateChip(donation.occurredAt),
            donation.amount.toString(),
            donation.paymentType,
            qboStatusLabel(qboStatus(donation)),
            donation.memo,
        ).joinToString(",") { field -> csvEscape(field) }
    }
    downloadCsv("$header\r\n$rows", "dot-drives-donations.csv")
}

private fun csvEscape(value: String): String =
    if (value.contains(',') || value.contains('"') || value.contains('\n')) {
        "\"${value.replace("\"", "\"\"")}\""
    } else {
        value
    }

private fun downloadCsv(csv: String, fileName: String) {
    val holder = js("({})")
    holder.csv = csv
    holder.fileName = fileName
    kotlinx.browser.window.asDynamic().__dotDrivesCsvExport = holder
    js(
        """{
          const h = window.__dotDrivesCsvExport;
          const blob = new Blob([h.csv], { type: 'text/csv;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = h.fileName;
          document.body.appendChild(link);
          link.click();
          link.remove();
          URL.revokeObjectURL(url);
          delete window.__dotDrivesCsvExport;
        }""",
    )
}
